import React, { useEffect, useState } from 'react'
import { ShareIcon } from '../../../lib/components/Icon'
import { getFormattedDate } from '../../../lib/utils/helpers'
import { getWebinarThumbnailUrl, getYoutubeThumbnailUrl } from '../webinar-thumbnail'

const DATE_FORMAT = 'MMMM DD, YYYY'

export type WebinarCardData = {
  id: number
  slug: string
  title: string
  description: string
  youtube_video_id: string
  thumbnail_url: string
  published_at: string
}

type WebinarCardProps = {
  webinar: WebinarCardData
  onClick: (webinar: WebinarCardData) => void
  onShare?: (webinar: WebinarCardData) => void
}

export const WebinarCard = ({ webinar, onClick, onShare }: WebinarCardProps) => {
  const [thumbnailSrc, setThumbnailSrc] = useState(() => getWebinarThumbnailUrl(webinar))

  useEffect(() => {
    setThumbnailSrc(getWebinarThumbnailUrl(webinar))
  }, [webinar])

  const handleShareClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onShare?.(webinar)
  }

  const handleThumbnailError = () => {
    const fallback = getYoutubeThumbnailUrl(webinar.youtube_video_id)
    if (thumbnailSrc !== fallback) {
      setThumbnailSrc(fallback)
    }
  }

  return (
    <article
      className='webinar-card'
      onClick={() => onClick(webinar)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick(webinar)
        }
      }}
      role='button'
      tabIndex={0}
      aria-label={`Play webinar: ${webinar.title}`}
      data-testid={`webinar-card-${webinar.id}`}
    >
      <div className='webinar-card__image-wrapper'>
        {onShare && (
          <button
            type='button'
            className='webinar-card__share'
            onClick={handleShareClick}
            aria-label={`Share webinar: ${webinar.title}`}
            data-testid={`webinar-share-${webinar.id}`}
          >
            <ShareIcon width='18' height='18' />
          </button>
        )}
        <img
          className='webinar-card__image'
          src={thumbnailSrc}
          alt={webinar.title}
          loading='lazy'
          onError={handleThumbnailError}
        />
        <span className='webinar-card__play-icon' aria-hidden='true'>
          <span />
        </span>
      </div>
      <div className='webinar-card__body'>
        <h3 className='webinar-card__title'>{webinar.title}</h3>
        <p className='webinar-card__description'>{webinar.description}</p>
        <span className='webinar-card__date'>
          {getFormattedDate(new Date(webinar.published_at), DATE_FORMAT)}
        </span>
      </div>
    </article>
  )
}
